import { fetchPgoReviews } from "../services/pgoApi";
import { renderCategoryScores, generateStars } from "../utils/pgoStarsUtils";

export const comparisonRenderer = (pgos) => ({
    loading: false,
    error: false,
    empty: false,
    showDifferences: false,
    sessionStorageKey: "selectedPgos",
    reviews: null,
    uuids: pgos.map((pgo) => pgo.uuid) || [],

    init() {
        this.syncSessionStorageWithUrl();
        this.getReviewData();
    },

    async getReviewData() {
        if (!this.uuids || !Array.isArray(this.uuids) || this.uuids.length === 0) {
            this.empty = true;
            console.error("Invalid or missing UUIDs");
            return;
        }

        this.loading = true;

        try {
            const reviews = await Promise.all(
                this.uuids.map((uuid, index) =>
                    uuid
                        ? fetchPgoReviews(uuid).then((review) => ({
                            review: review,
                            pgoId: pgos[index]?.id || null,
                        }))
                        : { pgoId: pgos[index]?.id || null, review: null }
                )
            );

            this.reviews = reviews;

            this.error = false;
        } catch (error) {
            this.error = true;
            console.error("Error fetching reviews:", error);
        } finally {
            this.loading = false;
            this.empty = false;
        }
    },

    toggleShowDifferences() {
        const rows = document.querySelectorAll(".checklist-item-row");
    
        rows.forEach((row) => {
            if (row.closest("#checklistReview")) return;
    
            const columns = Array.from(row.querySelectorAll(".checklist-item-column"));
            const classes = columns.map((column) => {
                const span = column.querySelector("span");
                return span ? span.className : null;
            });
            const areEqual = classes.every((cls) => cls === classes[0]);
    
            row.classList.toggle("hidden", this.showDifferences && areEqual);
        });
    
        const groups = document.querySelectorAll(".checklist-group");
    
        groups.forEach((group) => {
            const groupRows = group.querySelectorAll(".checklist-group-row");
    
            groupRows.forEach((groupRow) => {
                const itemRows = groupRow.querySelectorAll(".checklist-item-row");
                const allHidden = Array.from(itemRows).every((itemRow) =>
                    itemRow.classList.contains("hidden")
                );
    
                groupRow.classList.toggle("hidden", allHidden);
            });
    
            const allGroupRowsHidden = Array.from(groupRows).every((groupRow) =>
                groupRow.classList.contains("hidden")
            );
    
            group.classList.toggle("hidden", allGroupRowsHidden);
        });
    },    

    getCategoryNamesWithScores() {
        if (!Array.isArray(this.reviews)) {
            console.warn("Invalid reviews format.");
            return [];
        }

        const categoryMap = new Map();

        this.reviews.forEach(({ review, pgoId }, reviewIndex) => {
            const scores = review?.categoryScores || [];
            scores.forEach(({ name, score, sort }) => {
                if (!categoryMap.has(name)) {
                    categoryMap.set(name, {
                        sort,
                        scores: Array(this.reviews.length).fill({ html: null, score: null, pgoId: null }),
                    });
                }
                categoryMap.get(name).scores[reviewIndex] = {
                    stars: generateStars(score),
                    score: score ?? null,
                    pgoId,
                };
            });

            // Fill missing scores for reviews without categoryScores
            if (!scores.length) {
                categoryMap.forEach(({ scores }) => {
                    scores[reviewIndex] = { html: null, score: null, pgoId: null };
                });
            }
        });

        return [...categoryMap.entries()]
            .sort(([, a], [, b]) => a.sort - b.sort)
            .map(([name, { scores }]) => ({ name, scores }));
    },

    removeItem(pgoId) {
        // Remove all elements with the matching data-pgo-id
        document.querySelectorAll(`[data-pgo-id="${pgoId}"]`).forEach((element) => {
            element.remove();
        });

        // Update session storage and URL
        this.updateSessionStorage(pgoId);
        this.updateUrl();

        // Reset differences and fetch new data
        this.showDifferences = false;
        this.toggleShowDifferences();

        // Remove the review with the matching pgoId
        this.reviews = this.reviews.filter((review) => review.pgoId !== pgoId);

        // Check if no PGOs are left
        if (this.getSelectedPgos().length === 0) this.empty = true;
    },


    syncSessionStorageWithUrl() {
        const selectedPgos = new URLSearchParams(window.location.search).get("pgo")?.split(",") || [];
        sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(selectedPgos));
    },

    updateSessionStorage(pgoId) {
        const updatedPgos = this.getSelectedPgos().filter((id) => String(id) !== String(pgoId));
        sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(updatedPgos));
    },

    updateUrl() {
        const updatedQuery = this.getSelectedPgos().join(",");
        window.history.pushState({}, "", updatedQuery ? `${window.location.pathname}?pgo=${updatedQuery}` : window.location.pathname);
    },

    getSelectedPgos() {
        return JSON.parse(sessionStorage.getItem(this.sessionStorageKey)) || [];
    },

});